// Distance-domain model for the Track Difference visualization.
//
// The two laps never race on a shared clock here. At each station around the
// circuit we ask when each lap reached that station and how fast it was moving.
// That is the same-position comparison used by Time Delta and the comparison
// video export, and deliberately differs from Lap3D's same-time ghost race.

const EARTH_R_M = 6371000;
const M_TO_FT = 3.28084;
const DEFAULT_SAMPLES = 360;
const GAIN_EPS_S_PER_M = 0.00001;
const GAIN_BASELINE_M = 70;
const LIFT_GAMMA = 0.65;

/**
 * What the ribbon height means. Four quantities, and they answer different
 * questions — which is why the view lets you switch rather than picking one.
 *
 * `gap` is the plain running delta, the trace every driver already reads: how
 * many seconds ahead or behind, right here. It is a *state*, so it says where
 * someone stands rather than where the time went — a lap held 0.6 s up stays
 * tall through corners that were actually lost.
 *
 * `gain` is that quantity's slope, and answers "where did the time go": its
 * area under the curve is the final gap, so a tall stretch really is where the
 * lap was won.
 *
 * `line` leaves time out of it entirely: it is how far apart the two cars ran
 * across the road, which is the dimension the other three cannot show at all.
 *
 * `speed` is the most visceral and the least trustworthy of the four — the
 * driver carrying more speed into a corner is frequently the one losing time.
 * That is exactly why color follows whichever one is driving the height.
 */
export const HEIGHT_MODES = {
  gap: {
    key: 'gap',
    label: 'Gap',
    field: 'gapSec',
    autoField: 'gapScaleS',
    referenceScale: 1,      // seconds ahead at 1x — full ribbon height
    evenEps: 0.005,
    hint: 'Seconds ahead or behind at this point. Where each lap stands — not where the time went.',
  },
  gain: {
    key: 'gain',
    label: 'Gain',
    field: 'gainPerKmS',
    autoField: 'gainScaleSPerKm',
    referenceScale: 3,      // seconds per km at 1x — full ribbon height
    evenEps: 0.02,
    hint: 'Seconds gained over 500 ft of track. The area under the ribbon is the final gap, so this is where the lap was won.',
  },
  line: {
    key: 'line',
    label: 'Line',
    field: 'lineOffsetM',
    autoField: 'lineScaleM',
    referenceScale: 3,      // metres apart at 1x — full ribbon height
    evenEps: 0.15,
    hint: 'How far apart the two racing lines run, measured across the track. Color marks which lap is on the left.',
  },
  speed: {
    key: 'speed',
    label: 'Speed',
    field: 'speedDeltaMph',
    autoField: 'speedScaleMph',
    referenceScale: 20,     // mph at 1x — full ribbon height
    evenEps: 0.25,
    hint: 'Speed difference at the same point on track. Vivid, but more speed into a corner often means less time gained.',
  },
};

/**
 * The metric value that fills the ribbon to full height. `auto` fits this
 * pair's own 95th percentile (always visible, never comparable between pairs —
 * which is why the view labels the resulting scale); a numeric exaggeration
 * divides the mode's fixed reference so heights stay comparable across pairs.
 */
export function heightFullScale(model, modeKey, exaggeration) {
  const mode = HEIGHT_MODES[modeKey] || HEIGHT_MODES.gap;
  if (exaggeration === 'auto') {
    return Number(model?.[mode.autoField]) || mode.referenceScale;
  }
  return mode.referenceScale / (Number(exaggeration) || 1);
}

/**
 * Signed 0..1 ribbon response. Most of a lap sits inside a couple of mph, so a
 * linear map leaves the everyday difference in the noise floor; the gamma lifts
 * it without letting a straight-line blowout run off the top of the frame.
 */
export function liftResponse(ratio) {
  const value = Number(ratio);
  if (!Number.isFinite(value)) return 0;
  return Math.sign(value) * Math.min(1, Math.abs(value)) ** LIFT_GAMMA;
}

const finite = value => Number.isFinite(Number(value));

function cleanTrace(trace) {
  const points = (trace?.points || [])
    .filter(p => finite(p?.dist_m) && finite(p?.time) && finite(p?.lat) && finite(p?.lon))
    .map(p => ({
      distM: Number(p.dist_m),
      time: Number(p.time),
      speedMph: finite(p.speed) ? Number(p.speed) : null,
      csvTime: finite(p.csv_time) ? Number(p.csv_time) : null,
      lat: Number(p.lat),
      lon: Number(p.lon),
    }))
    .sort((a, b) => a.distM - b.distM);

  // Interpolation needs a strictly increasing distance axis. Keep the last
  // sample at a repeated station because it is normally the freshest value.
  const unique = [];
  for (const point of points) {
    if (unique.length && Math.abs(point.distM - unique[unique.length - 1].distM) < 1e-6) {
      unique[unique.length - 1] = point;
    } else {
      unique.push(point);
    }
  }
  if (unique.length < 3) return null;
  const startM = unique[0].distM;
  const endM = unique[unique.length - 1].distM;
  if (!(endM > startM)) return null;
  const rawTime = unique[unique.length - 1].time - unique[0].time;
  if (!(rawTime > 0)) return null;

  const declaredTime = finite(trace?.lap_time_seconds) && Number(trace.lap_time_seconds) > 0
    ? Number(trace.lap_time_seconds)
    : rawTime;
  // A lap trace commonly stops one logger sample before the timing beacon.
  // Scale the relative clock by that small residual so the final displayed gap
  // agrees exactly with the real lap times rather than jumping at the line.
  const timeScale = declaredTime / rawTime;
  const time0 = unique[0].time;
  unique.forEach(point => { point.relTime = (point.time - time0) * timeScale; });

  return { points: unique, startM, endM, lengthM: endM - startM, timeS: declaredTime };
}

function interpolate(points, distM, field) {
  if (distM <= points[0].distM) return points[0][field];
  const last = points[points.length - 1];
  if (distM >= last.distM) return last[field];
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].distM <= distM) lo = mid; else hi = mid;
  }
  const span = points[hi].distM - points[lo].distM;
  if (!(span > 0)) return points[lo][field];
  const t = (distM - points[lo].distM) / span;
  const a = points[lo][field];
  const b = points[hi][field];
  if (!finite(a)) return finite(b) ? b : null;
  if (!finite(b)) return a;
  return Number(a) + (Number(b) - Number(a)) * t;
}

function smoothNullable(values, radius = 2) {
  return values.map((value, index) => {
    if (!finite(value)) return null;
    let total = 0;
    let count = 0;
    for (let i = Math.max(0, index - radius); i <= Math.min(values.length - 1, index + radius); i += 1) {
      if (!finite(values[i])) continue;
      total += Number(values[i]);
      count += 1;
    }
    return count ? total / count : Number(value);
  });
}

function cleanElevationProfile(profile) {
  return (profile || [])
    .filter(point => finite(point?.dist_frac) && finite(point?.altitude_m))
    .map(point => ({ fraction: Number(point.dist_frac), altitudeM: Number(point.altitude_m) }))
    .filter(point => point.fraction >= 0 && point.fraction <= 1)
    .sort((a, b) => a.fraction - b.fraction);
}

function elevationAt(profile, fraction) {
  if (!profile.length) return null;
  if (fraction <= profile[0].fraction) return profile[0].altitudeM;
  const last = profile[profile.length - 1];
  if (fraction >= last.fraction) return last.altitudeM;
  let lo = 0;
  let hi = profile.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (profile[mid].fraction <= fraction) lo = mid; else hi = mid;
  }
  const span = profile[hi].fraction - profile[lo].fraction;
  const t = span > 0 ? (fraction - profile[lo].fraction) / span : 0;
  return profile[lo].altitudeM + (profile[hi].altitudeM - profile[lo].altitudeM) * t;
}

function stationIndexAtFraction(stations, fraction) {
  return Math.max(0, Math.min(stations.length - 1, Math.round(fraction * (stations.length - 1))));
}

function sectorRows(stations, sectors) {
  return (sectors || [])
    .filter(s => finite(s?.start_frac) && finite(s?.end_frac) && Number(s.end_frac) > Number(s.start_frac))
    .map((sector, index) => {
      const startFrac = Math.max(0, Math.min(1, Number(sector.start_frac)));
      const endFrac = Math.max(startFrac, Math.min(1, Number(sector.end_frac)));
      const start = stations[stationIndexAtFraction(stations, startFrac)];
      const end = stations[stationIndexAtFraction(stations, endFrac)];
      const aTime = end.timeA - start.timeA;
      const bTime = end.timeB - start.timeB;
      return {
        id: sector.id || `S${index + 1}`,
        label: sector.label || `Sector ${index + 1}`,
        startFrac,
        endFrac,
        gapSec: bTime - aTime, // positive => A completed the sector sooner
      };
    });
}

/**
 * Build one render-ready comparison from two explicit lap descriptors.
 * Descriptors need `{ key, name, accent, trace }`; lap/session labels are
 * carried through untouched for the UI.
 */
export function buildTrackDifferenceModel(lapA, lapB, {
  samples = DEFAULT_SAMPLES,
  sectors = [],
  elevationProfile = [],
} = {}) {
  const a = cleanTrace(lapA?.trace);
  const b = cleanTrace(lapB?.trace);
  if (!a || !b || lapA?.key === lapB?.key) return null;

  const count = Math.max(80, Math.min(720, Math.round(samples)));
  const raw = [];
  for (let i = 0; i < count; i += 1) {
    const fraction = i / (count - 1);
    const distA = a.startM + fraction * a.lengthM;
    const distB = b.startM + fraction * b.lengthM;
    raw.push({
      index: i,
      fraction,
      distM: distA,
      distFt: distA * M_TO_FT,
      timeA: interpolate(a.points, distA, 'relTime'),
      timeB: interpolate(b.points, distB, 'relTime'),
      speedA: interpolate(a.points, distA, 'speedMph'),
      speedB: interpolate(b.points, distB, 'speedMph'),
      lat: interpolate(a.points, distA, 'lat'),
      lon: interpolate(a.points, distA, 'lon'),
      latB: interpolate(b.points, distB, 'lat'),
      lonB: interpolate(b.points, distB, 'lon'),
    });
  }

  const speedA = smoothNullable(raw.map(s => s.speedA));
  const speedB = smoothNullable(raw.map(s => s.speedB));
  const profile = cleanElevationProfile(elevationProfile);
  const elevations = raw.map(station => elevationAt(profile, station.fraction));
  const finiteElevations = elevations.filter(finite).map(Number);
  const elevationMinM = finiteElevations.length ? Math.min(...finiteElevations) : 0;
  const elevationMaxM = finiteElevations.length ? Math.max(...finiteElevations) : 0;
  const lat0 = raw.reduce((sum, s) => sum + s.lat, 0) / raw.length;
  const lon0 = raw.reduce((sum, s) => sum + s.lon, 0) / raw.length;
  const cosLat = Math.cos((lat0 * Math.PI) / 180);
  const referenceIsA = a.timeS <= b.timeS;
  // Cross-panel distance is normally expressed on the primary session's lap
  // axis. Preserve that convention even if the user puts the primary lap in
  // the B selector, while keeping A's GPS line as the visual geometry.
  const distanceUsesB = lapB?.driverKey === 'p' && lapA?.driverKey !== 'p';

  // Central difference over a ~70 m baseline rather than between neighbouring
  // stations. At 400-odd stations a lap the adjacent step is under 10 m, where
  // the derivative is mostly interpolation noise - and the ribbon draws this
  // rate directly, so a spiky rate is a spiky ribbon.
  const gaps = raw.map(station => station.timeB - station.timeA);
  const stepM = a.lengthM / (count - 1);
  const halfWindow = Math.max(1, Math.round(GAIN_BASELINE_M / Math.max(stepM, 1e-6) / 2));
  const rawGainRates = raw.map((station, index) => {
    const lo = Math.max(0, index - halfWindow);
    const hi = Math.min(raw.length - 1, index + halfWindow);
    const ds = raw[hi].distM - raw[lo].distM;
    return ds > 0 ? (gaps[hi] - gaps[lo]) / ds : 0;
  });
  const gainRates = smoothNullable(rawGainRates, 2);

  // Both laps in one local metric frame, so the two racing lines can be drawn
  // and compared. B's position was already interpolated per station and, until
  // now, only ever forwarded to the cross-panel cursor.
  const toX = lon => ((lon - lon0) * Math.PI / 180) * EARTH_R_M * cosLat;
  const toY = lat => ((lat - lat0) * Math.PI / 180) * EARTH_R_M;
  const xA = raw.map(station => toX(station.lon));
  const yA = raw.map(station => toY(station.lat));
  const xB = raw.map(station => toX(station.lonB));
  const yB = raw.map(station => toY(station.latB));

  // How far apart the two lines run, measured *perpendicular* to A's direction
  // of travel. Stations are matched by lap fraction, so a wider line is further
  // along in absolute metres at the same fraction; that along-track component is
  // an artifact of the matching, and only the perpendicular part is a real
  // difference in line. Positive => A is to the left of B, matching the
  // convention the other metrics use, where positive is A's side of the story.
  const rawLineOffsets = raw.map((_, index) => {
    const prev = Math.max(0, index - 1);
    const next = Math.min(raw.length - 1, index + 1);
    const hx = xA[next] - xA[prev];
    const hy = yA[next] - yA[prev];
    const heading = Math.hypot(hx, hy);
    if (!(heading > 0)) return 0;
    return ((hx * (yA[index] - yB[index])) - (hy * (xA[index] - xB[index]))) / heading;
  });
  // Both traces carry their own GPS noise, so the difference carries twice it.
  const lineOffsets = smoothNullable(rawLineOffsets, 2);

  const stations = raw.map((station, index) => {
    const gapSec = gaps[index];
    const gainRateA = gainRates[index];
    const speedDeltaMph = finite(speedA[index]) && finite(speedB[index])
      ? speedA[index] - speedB[index]
      : null;
    const winner = gainRateA > GAIN_EPS_S_PER_M ? 'a'
      : gainRateA < -GAIN_EPS_S_PER_M ? 'b'
      : finite(speedDeltaMph) && Math.abs(speedDeltaMph) >= 0.25
        ? (speedDeltaMph > 0 ? 'a' : 'b')
        : 'even';
    return {
      ...station,
      speedA: speedA[index],
      speedB: speedB[index],
      speedDeltaMph,
      // Seconds gained per kilometre of track. Positive => A is pulling away.
      gainPerKmS: gainRateA * 1000,
      csvTimeA: interpolate(a.points, station.distM, 'csvTime'),
      csvTimeB: interpolate(b.points, b.startM + station.fraction * b.lengthM, 'csvTime'),
      gapSec,
      gainRateA,
      winner,
      clockSec: referenceIsA ? station.timeA : station.timeB,
      xM: xA[index],
      yM: yA[index],
      xBM: xB[index],
      yBM: yB[index],
      // Signed: positive => A runs to the left of B. Magnitude is how far apart.
      lineOffsetM: lineOffsets[index],
      lineSepM: Math.abs(lineOffsets[index]),
      zM: finite(elevations[index]) ? Number(elevations[index]) - elevationMinM : 0,
      distFt: (distanceUsesB
        ? b.startM + station.fraction * b.lengthM
        : station.distM) * M_TO_FT,
    };
  });

  // Robust visual scale: one GPS spike should not flatten the rest of the lap.
  // The floor is the other half of that guard - without it an evenly matched
  // pair gets amplified until its noise looks like a finding.
  const percentile95 = (values, floor) => {
    const sorted = values.filter(finite).map(value => Math.abs(Number(value))).sort((x, y) => x - y);
    const value = sorted.length ? sorted[Math.max(0, Math.ceil(sorted.length * 0.95) - 1)] : 0;
    return Math.max(floor, value);
  };
  const xs = stations.map(station => station.xM);
  const ys = stations.map(station => station.yM);

  return {
    lapA,
    lapB,
    stations,
    sectors: sectorRows(stations, sectors),
    durationSec: referenceIsA ? a.timeS : b.timeS,
    reference: referenceIsA ? 'a' : 'b',
    canonicalStartM: distanceUsesB ? b.startM : a.startM,
    canonicalLengthM: distanceUsesB ? b.lengthM : a.lengthM,
    finalGapSec: b.timeS - a.timeS,
    speedScaleMph: percentile95(stations.map(station => station.speedDeltaMph), 3),
    gainScaleSPerKm: percentile95(stations.map(station => station.gainPerKmS), 0.35),
    gapScaleS: percentile95(stations.map(station => station.gapSec), 0.15),
    lineScaleM: percentile95(stations.map(station => station.lineOffsetM), 0.5),
    // Longest horizontal extent of the circuit, so ribbon height can be sized
    // against the track rather than against a fixed pixel count.
    spanM: Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys), 1),
    elevationRangeM: elevationMaxM - elevationMinM,
  };
}

export function stationAtElapsed(model, elapsedSec) {
  const stations = model?.stations || [];
  if (!stations.length) return null;
  const target = Math.max(0, Math.min(model.durationSec, Number(elapsedSec) || 0));
  let lo = 0;
  let hi = stations.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (stations[mid].clockSec <= target) lo = mid; else hi = mid;
  }
  return target - stations[lo].clockSec <= stations[hi].clockSec - target
    ? stations[lo] : stations[hi];
}

export function stationAtDistanceFt(model, distFt) {
  const stations = model?.stations || [];
  if (!stations.length || !finite(distFt)) return null;
  const target = Number(distFt);
  let lo = 0;
  let hi = stations.length - 1;
  if (target <= stations[lo].distFt) return stations[lo];
  if (target >= stations[hi].distFt) return stations[hi];
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (stations[mid].distFt <= target) lo = mid; else hi = mid;
  }
  return target - stations[lo].distFt <= stations[hi].distFt - target
    ? stations[lo] : stations[hi];
}

/** Default to each driver's best lap, or the two best laps when only one driver is available. */
export function pickDefaultTrackDifferencePair(options) {
  const lapTime = option => Number(option?.timeS ?? option?.trace?.lap_time_seconds);
  const valid = (options || []).filter(option => finite(lapTime(option)) && lapTime(option) > 0);
  if (valid.length < 2) return [];
  const fastestByDriver = [];
  for (const option of valid) {
    const found = fastestByDriver.find(o => o.driverKey === option.driverKey);
    if (!found) fastestByDriver.push(option);
    else if (lapTime(option) < lapTime(found)) {
      fastestByDriver[fastestByDriver.indexOf(found)] = option;
    }
  }
  const primary = fastestByDriver.find(o => o.driverKey === 'p') || fastestByDriver[0];
  const rivals = fastestByDriver
    .filter(o => o.key !== primary.key)
    .sort((x, y) => lapTime(x) - lapTime(y));
  if (rivals.length) return [primary, rivals[0]];
  return [...valid].sort((x, y) => lapTime(x) - lapTime(y)).slice(0, 2);
}
